<template>
    <TransitionRoot
        :show="isOpen"
        as="template"
    >
        <Dialog
            class="relative z-70"
            @close="closable ? close() : () => {}"
        >
            <TransitionChild
                as="template"
                enter="ease-out duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in duration-200"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 bg-gray-500/75 transition-opacity print:static" />
            </TransitionChild>

            <div class="fixed inset-0 z-70 w-screen overflow-y-auto print:static">
                <div class="flex min-h-full items-end justify-center text-center sm:items-center sm:p-0">
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enter-to="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 sm:scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <DialogPanel
                            :class="size"
                            class="flex flex-col relative transform overflow-hidden rounded-t-2xl sm:rounded-none mt-16 sm:mt-0 transition-all sm:my-8 w-full sm:p-8 sm:mx-8"
                        >
                            <div
                                v-if="closable && !loading"
                                class="flex justify-end mb-4"
                            >
                                <a
                                    class="block p-3 rounded-full transition-colors bg-white hover:bg-black group cursor-pointer z-80 print:hidden"
                                    @click="close()"
                                >
                                    <icon-times
                                        class="w-4 h-auto transition-colors text-black group-hover:text-white"
                                    />
                                </a>
                            </div>

                            <div
                                :class="modalClasses"
                                class="bg-white px-8 pt-14 pb-8 lg:px-12 lg:py-20 lg:w-full text-left shadow-xl rounded-2xl"
                            >
                                <div class="pt-4 sm:pt-0">
                                    <div :class="{ 'text-center': center }">
                                        <DialogTitle
                                            as="h3"
                                            class="text-xl leading-7 font-medium-light uppercase mb-0"
                                        >
                                            <slot name="title" />
                                        </DialogTitle>
                                        <div :class="{ 'mt-2': slots.title }">
                                            <slot name="default" />
                                        </div>
                                    </div>
                                </div>
                                <div
                                    v-if="slots.actions"
                                    class="pt-5 sm:pt-6 px-6 -mx-6 shadow-top"
                                >
                                    <slot name="actions" />
                                </div>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script lang="ts" setup>
import IconTimes from "~/public/icons/times.svg";

import { ref } from "vue";
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from "@headlessui/vue";

withDefaults(
    defineProps<{
        size?: string;
        center?: boolean;
        closable?: boolean;
        loading?: boolean;
        modalClasses?: string;
    }>(),
    {
        size: "sm:max-w-md",
        center: true,
        closable: true,
        loading: false,
        modalClasses: "",
    },
);

const emit = defineEmits(["opening", "opened", "closing", "closed"]);

const slots = useSlots();
const isOpen = ref(false);

const toggle = () => {
    isOpen.value = !isOpen.value;
};

const open = () => {
    emit("opening");
    isOpen.value = true;
    emit("opened");
};

const close = () => {
    emit("closing");
    isOpen.value = false;
    emit("closed");
};

defineExpose({
    toggle,
    open,
    close,
});
</script>

<style>
@reference "../../assets/css/app.css";

.shadow-top {
    box-shadow: 0 -2px 4px 0 #0000000d;
}
</style>
